import { all, call, put, select } from 'redux-saga/effects';
import { CONTACT_LIMIT_EVENT, contactLimitFrom, OVER_LIMIT_EVENT, overLimitFrom } from '../../utils/contactLimit';
import { ONBOARDING_ROOT } from '../../backend/sagas/onboardingRedirect';

// The three saga success patterns a contact delete can land on (single row,
// bulk-by-ids, bulk-exclude-ids — see householdsSaga.tsx's deleteHousehold /
// deleteHouseholdsByIds / deleteAllHouseholdsExcludeIds). Every one of these
// goes through THIS transport (ApiService.call via SagaWrapper), never
// api.ts's `instance` — api.ts exports an unused `deleteHouseholdsByIds`
// helper that nothing in the app actually calls, so its own success
// interceptor's over-limit refresh is effectively decorative for deletes;
// this is the one place that fires for real (Task 17, browser-verified
// 2026-09-15: a delete from the Contacts page never refreshed /me until this
// was added here).
const CONTACT_DELETE_SUCCESS_PATTERNS = new Set([
  'DELETE_HOUSEHOLD_SUCCESS',
  'DELETE_HOUSEHOLDS_BY_IDS_SUCCESS',
  'DELETE_ALL_HOUSEHOLDS_EXCLUDE_IDS_SUCCESS',
]);

export const createResponseDispatcher = (action, errorListeners) => {
  return function* ({ data, error }, successPattern, errorPattern, overrideMeta) {
    const { meta, callbacks } = action;

    if (error) {
      // Every saga-driven write (Tier 1: households, persons, visits, routes...)
      // lands here on failure. This is the saga world's counterpart to api.ts's
      // response interceptor — that one only sees requests made through
      // backend/api/api.ts's own axios `instance`; anything dispatched as a
      // redux action goes through the SEPARATE transport in
      // lib/services/ApiService.js (its own bare `axios(...)` call, no shared
      // interceptors), so a contact-limit 422 from e.g. the Contacts page's
      // "New Contact" form only ever reaches the app-wide ContactLimitDialog if
      // it's also dispatched from here. Same never-during-onboarding guard as
      // api.ts (nothing here runs during registration today, since
      // ContactIntakePanels intentionally bypasses redux/sagas, but this keeps
      // the two dispatch sites' behaviour identical rather than assuming that).
      const limitInfo = contactLimitFrom(error) || contactLimitFrom(error?.error);
      if (limitInfo && !window.location.pathname.startsWith(ONBOARDING_ROOT)) {
        window.dispatchEvent(new CustomEvent(CONTACT_LIMIT_EVENT, { detail: limitInfo }));
      }

      // View-only refusal (Task 16/17): mirrors api.ts's interceptor for this
      // second transport. Every household edit (UPDATE_HOUSEHOLD_TRIGGER) and
      // visit report (UPDATE_VISIT_TRIGGER) saga lands here on failure, and
      // that is exactly the pair of writes the backend now guards with 403
      // OVER_CONTACT_LIMIT — without this, an over-limit account editing a
      // contact or filing a report from the normal (online) app would see no
      // explanation at all.
      const overLimit = overLimitFrom(error) || overLimitFrom(error?.error);
      if (overLimit && !window.location.pathname.startsWith(ONBOARDING_ROOT)) {
        window.dispatchEvent(new CustomEvent(OVER_LIMIT_EVENT, { detail: overLimit }));
      }

      yield all(errorListeners.map(l => call(l, data, error)));

      yield put({
        type: errorPattern,
        payload: error,
        meta: overrideMeta || meta
      });

      if (callbacks?.onError) {
        yield call(callbacks.onError, error);
      }
      return;
    }

    yield put({
      type: successPattern,
      payload: data,
      meta: overrideMeta || meta
    });

    // View-only self-lift (Task 16/17): deleting contacts is how an
    // over-limit account gets itself back within its plan, and every delete
    // path goes through this transport (see CONTACT_DELETE_SUCCESS_PATTERNS
    // above). Refresh /me right after one so the banner/dialog lift the
    // moment the account fits again, gated on the store already being in
    // the over-limit state (cheap no-op the rest of the time).
    if (CONTACT_DELETE_SUCCESS_PATTERNS.has(successPattern)) {
      const contactLimitOver = yield select((state) => state.billingStore?.contactLimitOver);
      if (contactLimitOver) {
        yield put({ type: 'GET_USER' });
      }
    }

    if (callbacks?.onSuccess) {
      yield call(callbacks.onSuccess, data);
    }
  };
}
